import customConfig from "../config";

export const getRandomData = (
  length = 10,
  rangeStart = 1000,
  rangeStop = 10000,
) => {
  let retArr = [];
  for (let i = 0; i < length; i++) {
    retArr.push(Math.floor(Math.random() * rangeStop) + rangeStart);
  }
  return retArr;
};

export const getDateArr = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let datesArr = [];
  while (start <= end) {
    datesArr.push(start.toISOString().split("T")[0]);
    start.setDate(start.getDate() + 1);
  }
  return datesArr;
};

export const getCountryName = (countryCode) => {
  let countryCodeMapping = {
    BGD: "Bangladesh",
    IN: "India",
    JP: "Japan",
    SG: "Singapore",
    SL1: "Srilanka (Softlogic)",
    SL2: "Srilanka (Damro)",
    SL3: "Srilanka (Singer)",
    TH: "Thailand",
    NP: "Nepal",
  };

  if (countryCodeMapping.hasOwnProperty(countryCode)) {
    return countryCodeMapping[countryCode];
  }

  return countryCode;
};

export const getCountryCodeISO = (countryCode) => {
  let countryCodeISOMapping = {
    BD: "BGD",
    IN: "IND",
    JP: "JPN",
    SG: "SGP",
    SL1: "LKA",
    SL2: "LKA",
    SL3: "LKA",
    TH: "THA",
    NP: "NPL",
    LK: "LKA",
  };

  if (countryCodeISOMapping.hasOwnProperty(countryCode)) {
    return countryCodeISOMapping[countryCode];
  }

  return null;
};

/** Shared select options from auth.allowedCountries (same mapping as dealers). */
export const getAllowedCountrySelectOptions = (
  allowedCountries = [],
  { includeNone = false } = {}
) => {
  const options = (allowedCountries || [])
    .filter((country) => getCountryCodeISO(String(country).toUpperCase()))
    .map((country) => {
      const code = getCountryCodeISO(String(country).toUpperCase());
      return { value: code, label: code };
    });

  // Deduplicate by ISO code (e.g. SL1/SL2/SL3 → LKA).
  const unique = [
    ...new Map(options.map((option) => [option.value, option])).values(),
  ];

  return includeNone
    ? [{ value: "None", label: "None" }, ...unique]
    : unique;
};

export const getFormatedJSON = (data) => {
  return (
    <pre>
      <code>{JSON.stringify(data, null, 4)}</code>
    </pre>
  );
};

const formatSwitchStateEntry = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return null;
  }

  const channel = entry.rmcn ?? entry.channel ?? entry.id;
  const powerState =
    entry.rmcps ??
    entry.off ??
    entry.ps ??
    entry.state ??
    entry.power;

  if (channel != null && powerState != null && typeof powerState !== "object") {
    return `${channel}: ${powerState}`;
  }

  return null;
};

// used in DisplayDeviceStatusData for deviceStatusLogs cell values
export const formatStatusPayloadValue = (value) => {
  if (value == null || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "number" || typeof value === "string") {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "-";
    }
    return value.map((item) => formatStatusPayloadValue(item)).join(", ");
  }

  if (typeof value === "object") {
    if (Object.prototype.hasOwnProperty.call(value, "value") && Object.keys(value).length === 1) {
      return formatStatusPayloadValue(value.value);
    }

    const switchStateLabel = formatSwitchStateEntry(value);
    if (switchStateLabel) {
      return switchStateLabel;
    }

    const primitiveEntries = Object.entries(value).filter(
      ([, entryValue]) =>
        entryValue != null &&
        (typeof entryValue === "string" ||
          typeof entryValue === "number" ||
          typeof entryValue === "boolean")
    );

    if (primitiveEntries.length > 0 && primitiveEntries.length === Object.keys(value).length) {
      return primitiveEntries.map(([key, entryValue]) => `${key}: ${entryValue}`).join(", ");
    }

    if (value.state != null && typeof value.state !== "object") {
      const channel = value.id ?? value.channel ?? value.ch ?? value.index;
      return channel != null ? `${channel}: ${value.state}` : String(value.state);
    }

    const keys = Object.keys(value);
    if (keys.length === 2) {
      const [firstKey, secondKey] = keys;
      const firstValue = value[firstKey];
      const secondValue = value[secondKey];
      if (typeof firstValue !== "object" && typeof secondValue !== "object") {
        return `${firstKey}: ${firstValue} (${secondValue})`;
      }
    }

    try {
      return JSON.stringify(value);
    } catch (error) {
      return "-";
    }
  }

  return String(value);
};

export const formatTableHeader = (header) => {
  //formatting based on _
  // const formattedHeader = header?.split("_")?.map(h => capitalize(h)?.join(" "))
  //formmatting based on capital letters
  // const formattedHeader = header?.split(/(?=[A-Z])/)?.join(" ")
  //add code is formatting of header is required
  return header;
};

export const getACDeviceStatusKeyMap = (key) => {
  const deviceStatusKeyMap = {
    acmd: "AC Mode",
    acfs: "Fan Speed",
    acfc: "Fan Controller",
    acdl: "Filter Dust Level",
    acsp: "Sleep Profile",
    acvs: "Vertical Swing",
    achs: "Horizontal Swing",
    actm: "Timer",
    ps: "Power Status",
    actmp: "Temperature (ºC)",
    acdc: "Display Control",
    acec: "Evaporator Clean",
    acng: "Nanoe GA",
    acpms: "PM2.5 Sensor",
    acpm: "Powerful Mode",
    rssi: "WiFi Strength",
    rmtmp: "Room Temperature (ºC)",
    acms: "Eco Navi",
    acmss: "Motion Sensor Status",
    acfh: "Time-based Filter Health",
    cnv: "Convertible Mode",
    mo: "Model ID",
    ty: "Device Type",
    ts: "Time Stamp",
  };

  return deviceStatusKeyMap[key] || key;
};

export function validatePassword(password) {
  const minLength = 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  if (password.length < minLength) {
    return {
      valid: false,
      error: `Password must be at least ${minLength} characters.`,
    };
  }
  if (!hasUppercase) {
    return {
      valid: false,
      error: "Password must include at least one uppercase letter.",
    };
  }
  if (!hasNumber) {
    return {
      valid: false,
      error: "Password must include at least one number.",
    };
  }
  if (!hasSpecial) {
    return {
      valid: false,
      error: "Password must include at least one special character.",
    };
  }
  return { valid: true };
}

export function normalizeTimestamp(timestamp) {
  if (!timestamp) return null;
  const ts = Number(timestamp);
  if (ts.toString().length === 10) {
    return ts * 1000;
  }
  return ts;
}

export const AllowedConversionType = {
  current_based: "Current Based",
  frequency_based: "Frequency Based",
};

export function convertCapabilityName(capabilityName) {
  return capabilityName
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim();
}

export const MAX_POSITIVE_INT = Number.MAX_SAFE_INTEGER;
export const MAX_POSITIVE_INT_LENGTH = String(MAX_POSITIVE_INT).length;

/**
 * validateJSONData is a JSON Validation utilities function
 */
const hasOnlyStringKeys = (obj) => {
  if (Array.isArray(obj)) return true;
  if (typeof obj === "object" && obj !== null) {
    return Object.keys(obj).every(
      (key) => typeof key === "string" && hasOnlyStringKeys(obj[key]),
    );
  }
  return true;
};

export const validateJSONData = (
  value,
  errorMsg = customConfig?.INVALID_JSON_MESSAGE,
) => {
  if (!value || !value.trim()) {
    return errorMsg;
  }
  try {
    const parsed = JSON.parse(value);
    if (!hasOnlyStringKeys(parsed)) {
      return errorMsg;
    }
    // Root must be a plain object (not array, not null)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return errorMsg;
    }
    return "";
  } catch {
    return errorMsg;
  }
};


/**
 * isJsonParseSuccessful is a utility function that checks whether JSON parsing succeeds.
 */
export const isJsonParseSuccessful = (value, { fallback = false, logError = false } = {}) => {
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value.trim());
  } catch (error) {
    if (logError) {
      console.warn("Failed to parse JSON:", {
        error: error.message,
        input: value,
      });
    }
    return fallback;
  }
};


/**
 * isJsonStringifySuccessful is a utility function that checks whether JSON parsing succeeds.
 */
export const isJsonStringifySuccessful = (value, { fallback = false, logError = false, formatted = false } = {}) => {
  try {
    if(formatted){
      return JSON.stringify(value, null, 2)
    }

    return JSON.stringify(value);
  } catch (error) {
    if (logError) {
      console.warn("Failed to stringify JSON:", {
        error: error.message,
        input: value,
      });
    }
    return fallback;
  }
};

/**
 * Converts a email list string to a proper array.
 * Handles:
 * - Backticks ` -> double quotes "
 * - Single quotes ' -> double quotes "
 * - ~ separators -> commas
 * - Invalid JSON with safe fallback to empty array
 *
 * @param {string} rawList - The raw string from env or secret manager
 * @returns {string[]} - Array of email strings
 */
export const parseEmailList = (rawList)  => {
  if (!rawList || typeof rawList !== "string") {
    return []; // return empty array for undefined/null/non-string
  }

  try {
    // Step 1: replace backticks and single quotes with double quotes
    let listStr = rawList.replace(/`/g, '"').replace(/'/g, '"');

    // Step 2: replace ~ with comma for JSON arrays
    listStr = listStr.replace(/~/g, ',');

    // Step 3: parse the JSON string
    const parsed = JSON.parse(listStr);

    // Step 4: ensure result is an array
    if (Array.isArray(parsed)) {
      return parsed;
    } else {
      console.warn("Parsed receipt list is not an array:", parsed);
      return [];
    }
  } catch (err) {
    console.error("Failed to parse receipt list:", err, rawList);
    return []; // fallback to empty array
  }
}
